/* eslint-disable @typescript-eslint/no-unused-vars */
import { useCallback, useMemo, useEffect, useState } from 'react'
import {
  BarChart, LineChart, PieChart, Table, Filter, Database, ArrowRight, Play, Save, Calculator, Clock3, SortAsc, Loader2, ArrowUpDown, ListFilter, PlusCircle, X, Info, AlertTriangle, Trash2, ChevronLeft, ChevronRight, ChevronDown, ChevronUp
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Tooltip, TooltipProvider, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip"
import { ANALYSIS_PROMPT_TEMPLATE } from '@/lib/prompts'
import { generateAIResponse } from '@/services/ai-api'
import { useCampaignData } from '@/contexts/campaign-data'
import type { Model } from '@/types/ai'
import { SHEET_TABS, REFINED_DATASOURCE_DEFINITIONS } from '@/lib/constants'
import { Checkbox } from "@/components/ui/checkbox"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Separator } from "@/components/ui/separator"
import type { DataSourceType, ColumnType, FilterType, SortConfigType, DataRowType, FilterOperatorType } from '@/types/prompt'
import { usePromptBuilderState } from '@/hooks/usePromptBuilderState'
import { PromptHeader } from '@/components/prompt/PromptHeader'
import { ConfigureAnalysis } from '@/components/prompt/ConfigureAnalysis'
import { AnalysisQuestions } from '@/components/prompt/AnalysisQuestions'
import { GeneratedPrompt } from '@/components/prompt/GeneratedPrompt'
import { AnalysisResult } from '@/components/prompt/AnalysisResult'

// Map the imported definitions for use in the component
const availableDataSources: DataSourceType[] = REFINED_DATASOURCE_DEFINITIONS
  .map(ds => ({ id: ds.id as string, name: ds.name })) // Assert ds.id as string

// Block type definitions
type BlockType =
  | 'data-source'
  | 'filter'
  | 'metric'
  | 'timeframe'
  | 'sort'

interface BlockOption {
  type: BlockType
  text: string
  icon: React.ComponentType<{ className?: string }>
  options?: string[]
  description: string
}

interface Block {
  id: string
  type: BlockType
  text: string
  selectedOption?: string
  isEnabled: boolean
}

// Available blocks configuration
const blockOptions: BlockOption[] = [
  {
    type: 'data-source',
    text: 'Data Source',
    icon: Database,
    options: ['Campaign Data', 'Ad Group Data', 'Keyword Data', 'Historical Performance'],
    description: 'Select the primary data source for analysis'
  },
  {
    type: 'filter',
    text: 'Filter Concept',
    icon: Filter,
    description: 'Enable to add specific filter criteria below'
  },
  {
    type: 'metric',
    text: 'Main Metrics',
    icon: LineChart,
    options: ['impr', 'clicks', 'cost', 'conv', 'value', 'CTR', 'CvR', 'AOV', 'ROAS', 'CPA'],
    description: 'Select primary metrics for analysis'
  },
  {
    type: 'timeframe',
    text: 'Time Frame',
    icon: Clock3,
    options: ['Last 7 Days', 'Last 30 Days', 'Last Quarter', 'Year to Date', 'Custom'],
    description: 'Set the time period for analysis'
  },
  {
    type: 'sort',
    text: 'Sort Concept',
    icon: SortAsc,
    description: 'Enable to add specific sort criteria below'
  }
]

interface BlockComponentProps {
  block: Block
  onOptionChange: (id: string, option: string) => void
  onToggle: (id: string) => void
}

const BlockComponent = ({ block, onOptionChange, onToggle }: BlockComponentProps) => {
  const blockOption = blockOptions.find(opt => opt.type === block.type)
  const BlockIcon = blockOption?.icon || Database

  return (
    <Card className="p-3 bg-white shadow-sm border hover:border-primary transition-colors">
      <div className="flex items-center gap-4 mb-2">
        <BlockIcon className="h-5 w-5 text-primary" />
        <span className="font-medium flex-grow">{block.text}</span>
        <Switch
          checked={block.isEnabled}
          onCheckedChange={() => onToggle(block.id)}
          aria-label={`Toggle ${block.text}`}
        />
      </div>
      <p className="text-xs text-muted-foreground mb-2">{blockOption?.description}</p>

      {block.isEnabled && blockOption?.options && blockOption.options.length > 0 && (
        <Select
          value={block.selectedOption}
          onValueChange={(value) => onOptionChange(block.id, value)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select option" />
          </SelectTrigger>
          <SelectContent>
            {blockOption.options.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </Card>
  )
}

// --- Main Component ---
export default function PromptBuilder() {
  // === Get external data FIRST ===
  const campaignDataContextValue = useCampaignData();
  // Destructure loadingStatus from the context
  const { data: allAccountData, loadingStatus: campaignDataLoadingStatus } = campaignDataContextValue || {};

  // === Use the Custom Hook ===
  const {
    selectedDataSource,
    columns,
    filters,
    editingFilterId,
    sortConfig,
    limitRows,
    generatedPromptText,
    filteredData,
    filteredRowCount,
    isLoading, // Combined loading state from hook
    isGeneratingPrompt,
    isSubmittingAnalysis,
    analysisResult,
    error,
    previewRowsPerPage,
    previewCurrentPage,
    showResultCard,
    setSelectedDataSource,
    setFilters,
    setEditingFilterId,
    setLimitRows,
    setPreviewRowsPerPage,
    setPreviewCurrentPage,
    setColumns,
    setFilteredData, // Need to pass these setters
    setFilteredRowCount,
    addFilter,
    updateFilter,
    removeFilter,
    handleSort,
    getFilterOperatorsForType,
    generatePrompt, // New function
    handleRunAnalysis, // Updated function
    selectedPredefinedQuestions,
    customQuestions,
    handlePredefinedQuestionChange,
    handleCustomQuestionChange,
    addCustomQuestion,
    removeCustomQuestion,
  } = usePromptBuilderState();

  // === Effects that bridge hook state and external data ===
  const rawData = useMemo(() => {
    if (!selectedDataSource || !allAccountData) return [];
    const dataForSheet = allAccountData[selectedDataSource.id as keyof typeof allAccountData] || [];
    return Array.isArray(dataForSheet) ? dataForSheet : [];
  }, [selectedDataSource, allAccountData]);

  // Effect to derive columns when rawData changes
  useEffect(() => {
    if (rawData && rawData.length > 0) {
      const firstRow = rawData[0];
      const derivedCols = Object.keys(firstRow).map(key => {
        const value = firstRow[key];
        let type: 'date' | 'dimension' | 'metric' = 'dimension';
        if (typeof value === 'number') { type = 'metric'; }
        else if (typeof value === 'string' && (key.toLowerCase().includes('date') || !isNaN(Date.parse(value)))) {
          const potentialDate = new Date(value);
          if (!isNaN(potentialDate.getTime()) && potentialDate.getFullYear() > 1990) {
            type = 'date';
          }
        }
        return { field: key, name: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '), type };
      });
      setColumns(derivedCols);
      setFilters([]);
      setEditingFilterId(null);
    } else {
      setColumns([]);
      setFilters([]);
      setEditingFilterId(null);
    }
  }, [rawData, setColumns, setFilters, setEditingFilterId]);

  // Effect to apply filters/sort - THIS RUNS HERE because it needs rawData
  useEffect(() => {
    let processedData = [...rawData];

    // Apply Filters
    if (filters.length > 0 && columns.length > 0) {
      processedData = processedData.filter(row => {
        return filters.every(filter => {
          if (!filter.field || !filter.operator) return true;

          const col = columns.find(c => c.field === filter.field);
          if (!col) return true;

          const rowValue = row[filter.field];
          const filterValue = filter.value;
          const colType = col.type;

          try {
            // If filter value is empty, treat filter as inactive
            if (filterValue === null || filterValue === undefined || filterValue === '') {
              return true;
            }

            // Handle null/undefined row values explicitly
            if (rowValue === null || rowValue === undefined) {
              if (filter.operator === 'equals' || filter.operator === 'equals_number') return false; // Null/undefined never equals a non-empty value
              if (filter.operator === 'not_equals') return true; // Null/undefined is not equal to a non-empty value
              // For other operators (>, <, contains, etc.), null/undefined doesn't match
              return false;
            }

            if (colType === 'metric') {
              const numRowValue = Number(rowValue);
              const numFilterValue = Number(filterValue);
              if (isNaN(numRowValue) || isNaN(numFilterValue)) return false;

              switch (filter.operator) {
                case 'greater_than_equals': return numRowValue >= numFilterValue;
                case 'greater_than': return numRowValue > numFilterValue;
                case 'less_than_equals': return numRowValue <= numFilterValue;
                case 'less_than': return numRowValue < numFilterValue;
                case 'equals_number': return numRowValue === numFilterValue;
                case 'not_equals': return numRowValue !== numFilterValue;
                default: return true;
              }
            } else if (colType === 'date') {
              const dateRowValue = new Date(rowValue);
              const dateFilterValue = new Date(filterValue);
              if (isNaN(dateRowValue.getTime()) || isNaN(dateFilterValue.getTime())) return false;

              dateRowValue.setHours(0, 0, 0, 0);
              dateFilterValue.setHours(0, 0, 0, 0);

              switch (filter.operator) {
                case 'greater_than_equals': return dateRowValue >= dateFilterValue;
                case 'greater_than': return dateRowValue > dateFilterValue;
                case 'less_than_equals': return dateRowValue <= dateFilterValue;
                case 'less_than': return dateRowValue < dateFilterValue;
                case 'equals': return dateRowValue.getTime() === dateFilterValue.getTime();
                case 'not_equals': return dateRowValue.getTime() !== dateFilterValue.getTime();
                default: return true;
              }
            } else {
              const strRowValue = String(rowValue).toLowerCase();
              const strFilterValue = String(filterValue).toLowerCase();
              switch (filter.operator) {
                case 'contains': return strRowValue.includes(strFilterValue);
                case 'does_not_contain': return !strRowValue.includes(strFilterValue);
                case 'equals': return strRowValue === strFilterValue;
                case 'not_equals': return strRowValue !== strFilterValue;
                default: return true;
              }
            }
          } catch (e) {
            console.error("Error applying filter:", filter, "to row:", row, e);
            return true;
          }
        });
      });
    }

    // Apply Sorting
    if (sortConfig.key && columns.find(c => c.field === sortConfig.key)) {
      const sortCol = columns.find(c => c.field === sortConfig.key);
      const sortType = sortCol?.type;

      processedData.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        let compare = 0;
        if (aValue === null || aValue === undefined) compare = -1;
        if (bValue === null || bValue === undefined) compare = 1;
        if (aValue === bValue) compare = 0;

        if (compare === 0) {
          if (sortType === 'metric') {
            compare = Number(aValue) - Number(bValue);
          } else if (sortType === 'date') {
            compare = new Date(aValue).getTime() - new Date(bValue).getTime();
          } else {
            compare = String(aValue).localeCompare(String(bValue));
          }
        }

        return sortConfig.direction === 'asc' ? compare : -compare;
      });
    }

    setFilteredData(processedData);
    setFilteredRowCount(processedData.length);

  }, [rawData, filters, sortConfig, columns, setFilteredData, setFilteredRowCount]);

  // Reset pagination when filters or data source change
  useEffect(() => {
    setPreviewCurrentPage(1);
  }, [filters, selectedDataSource, setPreviewCurrentPage]);

  // === Render Logic ===
  return (
    <TooltipProvider>
      <div className="container mx-auto p-4 space-y-6 max-w-6xl">
        <PromptHeader />

        <ConfigureAnalysis
          availableDataSources={availableDataSources}
          selectedDataSource={selectedDataSource}
          setSelectedDataSource={setSelectedDataSource}
          campaignDataLoadingStatus={campaignDataLoadingStatus}
          rawDataLength={rawData.length}
          filters={filters}
          filteredRowCount={filteredRowCount}
          columns={columns}
          addFilter={addFilter}
          removeFilter={removeFilter}
          updateFilter={updateFilter}
          editingFilterId={editingFilterId}
          setEditingFilterId={setEditingFilterId}
          getFilterOperatorsForType={getFilterOperatorsForType}
          limitRows={limitRows}
          setLimitRows={setLimitRows}
          filteredData={filteredData}
          previewRowsPerPage={previewRowsPerPage}
          setPreviewRowsPerPage={setPreviewRowsPerPage}
          previewCurrentPage={previewCurrentPage}
          setPreviewCurrentPage={setPreviewCurrentPage}
          sortConfig={sortConfig}
          handleSort={handleSort}
          isLoading={campaignDataLoadingStatus !== 'idle' || isLoading}
        />

        <AnalysisQuestions
          selectedPredefined={selectedPredefinedQuestions}
          handlePredefinedQuestionChange={handlePredefinedQuestionChange}
          customQuestions={customQuestions}
          handleCustomQuestionChange={handleCustomQuestionChange}
          addCustomQuestion={addCustomQuestion}
          removeCustomQuestion={removeCustomQuestion}
          handleGeneratePrompt={generatePrompt}
          isGeneratingPrompt={isGeneratingPrompt}
          isGenerateDisabled={!selectedDataSource || campaignDataLoadingStatus !== 'idle'}
        />

        <GeneratedPrompt
          promptText={generatedPromptText}
          isLoading={isGeneratingPrompt}
        />

        {generatedPromptText && (
          <div className="flex justify-end">
            <Button
              onClick={handleRunAnalysis}
              disabled={isSubmittingAnalysis || filteredData.length === 0}
              size="lg"
            >
              {isSubmittingAnalysis ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              Run Analysis
            </Button>
          </div>
        )}

        {showResultCard && (
          <AnalysisResult
            result={analysisResult}
            error={error}
            isLoading={isSubmittingAnalysis}
          />
        )}

        {error && !analysisResult && !isSubmittingAnalysis && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </div>
    </TooltipProvider>
  );
}